import { Container, Stack, Typography, alpha, styled } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { WorkspaceUser } from 'src/@types/user';
import { useAuthContext } from 'src/auth/useAuthContext';
import { Loading } from 'src/components/Loading/Loading';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLocales } from 'src/locales';
import { RTCConnection } from 'src/provider/VoiceChannel/livekit/types';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { GuestChannelInfo } from 'src/requests/gathering';
import {
  useLobbySocketEvent,
  useLobbySocketIO,
} from 'src/socket/LobbySocketProvider';
import { onAccepted, onRejected } from 'src/socket/listeners/gathering';
import { ConferenceDevicesVideo } from './ConferenceDevicesVideo';
import { JoinConferenceGuestForm } from './GuestForm';
import { JoinConferenceUserSelection } from './UserSelection';

type AcceptedSocketType = {
  guestAccessToken: string;
  voiceChannelToken: RTCConnection;
};

type Props = {
  gathering: GuestChannelInfo;
};

export function JoinConference({ gathering }: Props) {
  const [waiting, setWaiting] = useState<boolean>(false);
  const [rejected, setRejected] = useState<boolean>(false);

  const { isLoading } = useGatheringPermissions();

  const { io } = useLobbySocketIO();

  const { translate } = useLocales();
  const navigate = useNavigate();

  const { isAuthenticated, loginGuest, user } = useAuthContext();

  const { setConnectionObject, setConnectionStatus } = useLivekitRTCState();

  const handleJoin = async (user: WorkspaceUser) => {
    io.emit('/knock', {
      name: user.name,
      email: user.email,
      externalLink: gathering.externalLink,
      userId: user._id,
      workspaceId: user.workspace?._id,
    });

    setWaiting(true);

    sessionStorage.setItem('guest_user', JSON.stringify(user));
    await loginGuest(user);
  };

  useLobbySocketEvent(
    onAccepted((socket: AcceptedSocketType) => {
      setConnectionObject(socket.voiceChannelToken);
      setConnectionStatus('connecting');
      sessionStorage.setItem('accessToken', socket.guestAccessToken);
      navigate(`/gathering/join/${gathering._id}`);
    }),
  );

  useLobbySocketEvent(
    onRejected(() => {
      setRejected(true);
    }),
  );

  if (isLoading) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return (
    <CustomContainer>
      <ConferenceDevicesVideo />
      {waiting ? (
        <WaitingContainer>
          {rejected ? (
            <Typography variant="h3" sx={{ mb: 3, textAlign: 'center' }}>
              {translate('communicate.join_conference.ask_to_join_denied')}
            </Typography>
          ) : (
            <>
              <Typography variant="h3" sx={{ mb: 3, textAlign: 'center' }}>
                {translate('communicate.join_conference.ask_to_join_title')}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mb: 6, textAlign: 'center' }}
              >
                {translate('communicate.join_conference.ask_to_join_message')}
              </Typography>
              <Loading />
            </>
          )}
        </WaitingContainer>
      ) : (
        <Stack alignItems="center" width={1}>
          <Stack alignItems="center" mb={2}>
            <Typography align="center" variant="h3">
              “{gathering.name}” Gathering
            </Typography>
            <Typography align="center" paddingBottom={1} color="text.secondary">
              {translate('communicate.join_conference.form_title')}
            </Typography>
          </Stack>

          {isAuthenticated && !user?.wizard?.completedWizard && (
            <StyledWizardContainer>
              <Typography align="center" color="error.main">
                {translate('communicate.join_conference.no_workspace')}
              </Typography>
              <Typography align="center" color="error.dark">
                {translate('communicate.join_conference.join_name_email')}
              </Typography>
            </StyledWizardContainer>
          )}

          {!isAuthenticated || !user?.wizard?.completedWizard ? (
            <JoinConferenceGuestForm
              gathering={gathering}
              onSuccess={handleJoin}
            />
          ) : (
            <JoinConferenceUserSelection
              gathering={gathering}
              onSuccess={handleJoin}
            />
          )}
        </Stack>
      )}
    </CustomContainer>
  );
}

const CustomContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(5),
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing(10),
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(20),
  },
}));

const WaitingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(9),
  width: '100%',
}));

const StyledWizardContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.error.lighter}`,
  backgroundColor: alpha(theme.palette.error.darker, 0.1),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
  width: '100%',
}));
